import React, { useCallback, useEffect, useRef, useState } from "react";
import "./HomeScreen.css";
import MobileFilters from "../MobileFilters/MobileFilters";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { getDoctors } from "../../api/doctor";
import FilterChip from "./components/FilterChip";
import { Skeleton, Typography } from "@mui/material";
import { useAuth } from "../context/AuthContext";
import IsScrolling from "react-is-scrolling";
import { getFormattedAddress, makeQueryStringFromObject } from "../../utils";
import DoctorCardSkeleton from "./components/DoctorCardSkeleton";
import useAppContext from "../context/AppContext";
import HomeLocation from "./HomeLocation/HomeLocation/HomeLocation";
import HomeDocterCard from "./HomeDocterCard/HomeDocterCard";
import HomeDocCard from "./HomeDocterCard/HomeDocCard";

const PAGE_SIZE = 10;

export const initialState = {
  price: "",
  location: 0,
  speciality: [],
  sortBy: "",
  search: "",
};

const HomeScreen = (isScrolling) => {
  const { isAuthenticated, user } = useAuth();
  const { deviceLocation, setSelectedLocation, tempSelectedLocation } =
    useAppContext();
  const routerLocation = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [resp, setRespoense] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [doctorId, setDoctorId] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(false);
  const loadMoreRef = useRef(null);
  useEffect(() => {
    if (isScrolling) {
      setIsLocationDialogOpen(false);
    }
  }, [isScrolling]);

  useEffect(() => {
    const urlDoctorId = searchParams.get("id");
    if (urlDoctorId) {
      setDoctorId(urlDoctorId);
    }
  }, [searchParams]);

  useEffect(() => {
    const urlDoctorId = searchParams.get("id");
    if (!urlDoctorId && isAuthenticated && user) {
      if (
        user.role === "doctor" ||
        user.userType === "doctor" ||
        user.isDoctor
      ) {
        setDoctorId(user._id || user.id);
      }
    }
  }, [isAuthenticated, user, searchParams]);

  //State variables for Filters
  const [price, setPrice] = useState(
    routerLocation.state?.filters?.price ?? initialState.price
  );
  const [location, setLocation] = useState(
    routerLocation.state?.filters?.location ?? initialState.location
  );
  const [speciality, setSpeciality] = useState(
    routerLocation.state?.filters?.speciality ?? initialState.speciality
  );
  const [sortBy, setSortBy] = useState(
    routerLocation.state?.filters?.sortBy ?? initialState.sortBy
  );
  const [searchInput, setSearchInput] = useState(
    routerLocation.state?.filters?.search ?? initialState.search
  );
  const [search, setSearch] = useState(
    routerLocation.state?.filters?.search ?? initialState.search
  );

  const fetchDoctors = useCallback(async (requestedPage = 0, append = false) => {
    append ? setLoadingMore(true) : setLoading(true);
    const priceRange = price?.split("-");
    const minFees = priceRange?.[0];
    const maxFees = priceRange?.[1];

    const filter = {
      radius: location,
      minFees,
      maxFees,
      sortBy,
      specialities: speciality?.join(","),
      search,
    };

    let query = `?latitude=${deviceLocation?.lat}&longitude=${deviceLocation?.lng}`;
    query = makeQueryStringFromObject(filter, query);
    if (doctorId) {
      query += `&doctorId=${doctorId}`;
    }
    query += `&pageNo=${requestedPage}&pageSize=${PAGE_SIZE}`;
    // console.log("HomeScreen", query);

    const responseData = await getDoctors(query);
    // console.log("fetchDoctors", query, responseData?.data);
    if (responseData) {
      let doctorList = [];
      responseData?.data?.forEach((x) => {
        if (x.type === "HOSPITAL") {
          const filterData =
            x?.doctors?.filter(
              (z) => !doctorList.some((y) => y._id === z._id)
            ) || [];
          doctorList = [...doctorList, ...filterData];
        } else if (
          !doctorList.some((y) => y?._id === x?.doctor?._id) &&
          x?.doctor
        ) {
          doctorList.push(x.doctor);
        }
      });

      setDoctors((prev) => {
        const base = append ? [...prev, ...doctorList] : doctorList;
        return base.filter(
          (doc, idx, arr) => arr.findIndex((d) => d?._id === doc?._id) === idx
        );
      });

      setRespoense((prev) =>
        append ? [...prev, ...(responseData?.data || [])] : responseData?.data || []
      );
      setHasMore((responseData?.data || []).length === PAGE_SIZE);
      setPage(requestedPage);
    }
    append ? setLoadingMore(false) : setLoading(false);
  }, [price, location, speciality, sortBy, deviceLocation, search, doctorId]);

  useEffect(() => {
    if (doctorId && resp.length > 0) {
      const clinic = [];
      resp.forEach((x) => {
        if (x.type === "HOSPITAL" && x.doctors?.some((d) => d._id === doctorId)) {
          clinic.push(x);
        }
        if (x.type !== "HOSPITAL" && x.doctor?._id === doctorId) {
          clinic.push(x);
        }
      });
      setClinics(clinic);
    } else if (!doctorId) {
      setClinics([]);
    }
  }, [doctorId, resp]);

  const handleDeviceLocation = useCallback(async () => {
    if (!(deviceLocation?.lat && deviceLocation?.lng)) {
      setLoadingLocation(false);
      return;
    }

    setLoadingLocation(true);
    try {
      const formattedAddress = await getFormattedAddress(deviceLocation);
      setSelectedLocation({
        coordinates: deviceLocation,
        place: formattedAddress || tempSelectedLocation.place,
      });
    } finally {
      setLoadingLocation(false);
    }
  }, [deviceLocation, setSelectedLocation, tempSelectedLocation]);

  useEffect(() => {
    if (tempSelectedLocation?.place?.name !== "Error") {
      setLoadingLocation(false);
      return;
    }

    handleDeviceLocation();
  }, [handleDeviceLocation, tempSelectedLocation]);

  useEffect(() => {
    setPage(0);
    setHasMore(true);
    if (!doctorId) {
      setDoctors([]);
      setRespoense([]);
      setClinics([]);
    }
    fetchDoctors(0, false);
  }, [price, location, speciality, sortBy, deviceLocation, search, doctorId]); 

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearch(searchInput);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    const filters = routerLocation.state?.filters;
    if (filters) {
      setPrice(filters.price ?? initialState.price);
      setLocation(filters.location ?? initialState.location);
      setSpeciality(filters.speciality ?? initialState.speciality);
      setSortBy(filters.sortBy ?? initialState.sortBy);
      setSearch(filters.search ?? initialState.search);
      setSearchInput(filters.search ?? initialState.search);
    }
  }, [routerLocation.state]);

  // infinite scroll observer
  useEffect(() => {
    if (doctorId) return; // don't load more while viewing clinics
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchDoctors(page + 1, true);
        }
      },
      { threshold: 0.25 }
    );
    const currentRef = loadMoreRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => observer.disconnect();
  }, [doctorId, hasMore, loadingMore, loading, page, loadMoreRef, fetchDoctors]);

  // fallback scroll listener for environments where IntersectionObserver doesn't fire
  useEffect(() => {
    if (doctorId) return;
    const handleScroll = () => {
      if (loading || loadingMore || !hasMore) return;
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        fetchDoctors(page + 1, true);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [doctorId, loading, loadingMore, hasMore, page, fetchDoctors]);

  function onClickBack(doctorId) {
    setDoctorId(doctorId);
    setSearchParams({ id: doctorId });
  }

  function handleBackToList() {
    setSearchParams({});
    setDoctorId("");
    setClinics([]);
    setDoctors([]);
    setRespoense([]);
    setPage(0);
    setHasMore(true);
  }

  return (
    <div id="layout">
      {/* Location Bar */}
      <div className="home_profile_header d-flex justify-content-between align-items-start">
        {loadingLocation ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Skeleton
              variant="circular"
              width={30}
              height={30}
              sx={{ marginRight: "10px" }}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-around",
                flexDirection: "column",
                height: "2rem",
              }}
            >
              <Skeleton variant="rectangular" width={50} height={15} />
              <Skeleton variant="rectangular" width={90} height={8} />
            </div>
          </div>
        ) : (
          <HomeLocation
            setIsLocationDialogOpen={setIsLocationDialogOpen}
            isLocationDialogOpen={isLocationDialogOpen}
          />
        )}

        {/* No profile avatar here. The app bar's profile tab is the one place
            the session's patient is named and reached, so a second copy on the
            home page was the same control twice. Logged-out visitors still need
            a way in and the app bar has no login tab, so that CTA stays. */}
        {isAuthenticated ? null : (
          <button
            onClick={() => navigate("/login")}
            className="home_login_button"
          >
            Log In
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="home_search_input_container">
        <input
          type="text"
          className="home_search_input"
          placeholder="Search doctors by name or location..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <button onClick={() => setOpen(true)} className="home_input_filter_btn">
          <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
            <rect width="14" height="14" fill="url(#pattern0)" />
            <defs>
              <pattern
                id="pattern0"
                patternContentUnits="objectBoundingBox"
                width="1"
                height="1"
              >
                <use xlinkHref="#image0_468_292" transform="scale(0.0111111)" />
              </pattern>
              <image
                id="image0_468_292"
                width="90"
                height="90"
                xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAFoAAABaCAYAAAA4qEECAAAABmJLR0QA/wD/AP+gvaeTAAADdklEQVR4nO3az0sUcRjH8c/z3VG0Sx6qi6gs/guZBRJ1TboEbpE/TkFSp4JIlyU2MgsKj9Gli7WxsdeiQwVBHirtX4htjSA91C1rx3m6rBUpuuPOd+aZL8/ruM58fXhYZhd9A0oppZRSSimllFLuoKQHsIqZcrOfjoEwTkRDzOhu/OAz2CwYs/6wPJ19Hccozi565EY1Sxl6AOD4dtcx8CpDwbnydLZqcx4nFz0ys3yIKHgGwr7m7uBVE5gT5ULvkq2ZnFv0qZmPfZ4xiwDtD3nrCht/oDLVX7Mxl7FxaJI8Y+7vYskAcIDYuxf5QA1OvaNP36oNMPP7Vs6gIDjypJB9G9VMf85t5qKTxS97Ott+3QHRKIC9UQ8Rg58Mynvsl/w2IvJpDKCbANpbOPM7QCXuoCuVyz0/drrYa+bEzvb6HEDnWxgqUQwUK/neuX9eupubrbUDfLOFY7sAvkhr7AGY3OniHZ/RxSIbABMtDJQ84z/+/yU/8EsRnT7e2NH2I0T0y1In4zHH+fuaeEdTAGA+hlmsIfZGN73IbeORHM6Yb+xoW009o7nDXDJrXGfwGICuloeLG6OYm63WfUYJADLAGDFfa/HUbwAecae52szFTn29OzNTOxgYXmzlDANzuJzveRfVTH/PdUi50LvERM93ez8DT20sGXBs0QAAqk8CWNnFnV8zgXch6nE2OLfoylR/zQQ0DPBqiNtWmDBcLnQv25rLuUUDjUfIOgYBvGzi8heGgsHKdN8HmzM59WG4ldzt2lEKMMHEQ2D0AAABNWYsGKL5cr73TdIzKqWUUkpFJ9mvd4K6C9sSW7S07sK2RBYtsbuwLfZFS+0ubIv9bx1SuwvbYn1HS+4ubJPSddjoLmxLX9dhqbuwLYVdh93uwrZ0dx1xdxe2yeg6Au/sptei6i5sS1PXQcD13GzVj7i7sE1u1yG5u7At1me05O7Ctvg/DIV2F7bFvmip3YVtiXy9k9hd2JZ416HdhVJKKaVUlBL/ehdKijuQ1Cw67R1IKhbtQgciftGudCBi/5W1wZUORPQ72qUORErXEZaEDiR9XUdYQjqQFHYdYcnqQNLddYQlvQOR0XWEJakDSVPXEZaQDkRu1xGWSx2I6Ge0Sx2I6EUDcKYDEb9oVzoQ8YsG3OhARH8YbkU7EKWUUkoppZRSSqnk/Qa/yQUtVyL9swAAAABJRU5ErkJggg=="
              />
            </defs>
          </svg>
        </button>

        <MobileFilters
          open={open}
          setOpen={setOpen}
          price={price}
          setPrice={setPrice}
          location={location}
          setLocation={setLocation}
          sortBy={sortBy}
          setSortBy={setSortBy}
          speciality={speciality}
          setSpeciality={setSpeciality}
        />
      </div>

      {/* Filter Chips */}
      <div className="filter_chips">
        {price?.length > 0 && (
          <FilterChip
            label="Fees"
            setFilter={setPrice}
            initialState={initialState.price}
          />
        )}
        {location > 0 && (
          <FilterChip
            label="Location"
            setFilter={setLocation}
            initialState={initialState.location}
          />
        )}
        {speciality?.length > 0 && (
          <FilterChip
            label="Speciality"
            setFilter={setSpeciality}
            initialState={initialState.speciality}
          />
        )}
        {sortBy?.length > 0 && (
          <FilterChip
            label="Sort By"
            setFilter={setSortBy}
            initialState={initialState.sortBy}
          />
        )}
      </div>

      {/* Video Consultation Hidden */}

      {/* <VideoConsultationHome /> */}

      {/* Featured Doctors Hidden*/}

      {/* <FeaturedDoctors /> */}

      {/* Doctors Around You */}
      <div className="home_feature_docters">
        {doctorId ? (
          <div className="home_back_to_list">
            <button
              onClick={handleBackToList}
              className="home_back_to_list_btn"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.5 3.5L5 8L9.5 12.5"
                  stroke="#194AF5"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Back to doctors</span>
            </button>
          </div>
        ) : null}
        <h1 className="home_feature_docters_header new_11_sep">
          {doctorId ? "Choose a Clinic to Book Appointment" : "Doctors Around You"}
        </h1>
        {loading ? (
          Array(10)
            .fill("_")
            .map((_, i) => <DoctorCardSkeleton key={i} />)
        ) : !doctorId && doctors?.length ? (
          doctors?.map((doctor, index) => (
            <HomeDocCard
              doctor={doctor}
              key={index}
              onClickBack={onClickBack}
            />
          ))
        ) : doctorId && clinics?.length ? (
          clinics.map((clinic, index) => (
            <HomeDocterCard
              key={index}
              doctor={clinic}
              doctorId={doctorId}
              doctorDetails={doctors.find((x) => x._id === doctorId)}
              filters={{ price, location, speciality, sortBy, search }}
            />
          ))
        ) : (
          <div style={{ margin: "2rem" }}>
            <Typography variant="title">Doctors not found!</Typography>
          </div>
        )}
        <div ref={loadMoreRef} style={{ height: "24px" }} />
        {loadingMore && !doctorId ? (
          <div style={{ width: "90%", margin: "1rem auto" }}>
            <DoctorCardSkeleton />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default IsScrolling(HomeScreen);
